import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Edit, Loader2, Users, Search, ArrowLeft, Calendar, FileText, Upload, Download, Clock, MessageSquare, Star, Send } from "lucide-react";
import { toast } from "sonner";
import { usePagination, PaginationBar } from "@/components/PaginationBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BandiManagement({ userRole }) {
  const [bandi, setBandi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ titolo: "", descrizione: "", scadenza: "", tipo: "generale", requisiti: "", avanzamento: 0 });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  const fetchBandi = async () => {
    try { const { data } = await axios.get(`${API}/bandi`); setBandi(data); } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => {
    fetchBandi();
    axios.get(`${API}/admin/users`).then(r => setAllUsers(r.data)).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm({ titolo: "", descrizione: "", scadenza: "", tipo: "generale", requisiti: "", avanzamento: 0 }); setDetail(null); setShowForm(true); };
  const openEdit = (b) => { setEditing(b); setForm({ titolo: b.titolo, descrizione: b.descrizione, scadenza: b.scadenza || "", tipo: b.tipo, requisiti: b.requisiti || "", avanzamento: b.avanzamento || 0 }); setDetail(null); setShowForm(true); };

  const handleSave = async () => {
    if (!form.titolo || !form.descrizione) { toast.error("Compila titolo e descrizione"); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/bandi/${editing.id}`, form);
        toast.success("Bando aggiornato");
      } else {
        await axios.post(`${API}/bandi`, form);
        toast.success("Bando creato");
      }
      setShowForm(false); fetchBandi();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    try { await axios.delete(`${API}/bandi/${id}`); toast.success("Eliminato"); setDetail(null); fetchBandi(); } catch { toast.error("Errore"); }
  };

  const uploadDoc = async (e, bandoId) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${API}/upload/documento?tipo=bando&target_user_id=${bandoId}`, fd);
      toast.success("Documento caricato!");
    } catch { toast.error("Errore upload"); }
    e.target.value = '';
  };

  const getParticipantNames = (ids) => {
    return (ids || []).map(id => { const u = allUsers.find(x => x.id === id); return u ? `${u.nome} ${u.cognome}` : id; });
  };

  const daysLeft = (scadenza) => {
    if (!scadenza) return null;
    const diff = Math.ceil((new Date(scadenza) - new Date()) / (1000*60*60*24));
    return diff;
  };

  const searched = bandi.filter(b => `${b.titolo} ${b.tipo} ${b.stato}`.toLowerCase().includes(search.toLowerCase()));
  const pg = usePagination(searched);

  if (loading) return <div className="text-center py-12 text-white/30">Caricamento...</div>;

  // ─── Detail View ───
  if (detail) {
    const days = daysLeft(detail.scadenza);
    const participants = getParticipantNames(detail.partecipanti);
    const progress = detail.avanzamento || 0;
    return (
      <div data-testid="bando-detail">
        <button onClick={() => setDetail(null)} className="flex items-center gap-1.5 text-sm text-white/40 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Torna alla lista</button>

        <div className="bg-[#13243d] border border-[#1e3456] rounded-xl p-6 mb-4 shadow-lg shadow-black/20">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{detail.titolo}</h2>
              <p className="text-xs text-white/30 mt-1">Creato da {detail.created_by_name} il {new Date(detail.created_at).toLocaleDateString('it-IT')}</p>
            </div>
            <div className="flex gap-2">
              <Badge className={`text-xs border ${detail.stato === "aperto" ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>{detail.stato}</Badge>
              <Button size="sm" onClick={() => openEdit(detail)} className="bg-[#1a3a5c] text-[#5ba8d9] hover:bg-[#2a5080] border border-[#2a5080] text-xs" data-testid="edit-from-detail"><Edit className="w-3 h-3 mr-1" /> Modifica</Button>
            </div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <div className="bg-[#0f1d32] rounded-lg p-3 border border-[#1e3456]">
              <p className="text-[10px] text-white/30 uppercase mb-1">Tipo</p>
              <p className="text-sm text-white/70 font-medium">{detail.tipo}</p>
            </div>
            <div className="bg-[#0f1d32] rounded-lg p-3 border border-[#1e3456]">
              <p className="text-[10px] text-white/30 uppercase mb-1">Scadenza</p>
              <p className="text-sm text-white/70 font-medium">{detail.scadenza || "Nessuna"}</p>
              {days !== null && <p className={`text-[10px] mt-0.5 ${days > 7 ? 'text-emerald-400' : days > 0 ? 'text-amber-400' : 'text-red-400'}`}>{days > 0 ? `${days} giorni rimasti` : 'Scaduto'}</p>}
            </div>
            <div className="bg-[#0f1d32] rounded-lg p-3 border border-[#1e3456]">
              <p className="text-[10px] text-white/30 uppercase mb-1">Partecipanti</p>
              <p className="text-sm text-white/70 font-medium">{detail.partecipanti?.length || 0}</p>
            </div>
            <div className="bg-[#0f1d32] rounded-lg p-3 border border-[#1e3456]">
              <p className="text-[10px] text-white/30 uppercase mb-1">Avanzamento</p>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-2 bg-[#1e3456] rounded-full overflow-hidden">
                  <div className="h-full bg-[#5ba8d9] rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
                <span className="text-xs text-[#5ba8d9] font-bold">{progress}%</span>
              </div>
            </div>
          </div>

          {/* Descrizione */}
          <div className="mb-5">
            <p className="text-[10px] text-white/30 uppercase mb-2">Descrizione</p>
            <p className="text-sm text-white/60 leading-relaxed whitespace-pre-wrap bg-[#0f1d32] rounded-lg p-4 border border-[#1e3456]">{detail.descrizione}</p>
          </div>

          {detail.requisiti && (
            <div className="mb-5">
              <p className="text-[10px] text-white/30 uppercase mb-2">Requisiti</p>
              <p className="text-sm text-white/50 bg-[#0f1d32] rounded-lg p-4 border border-[#1e3456]">{detail.requisiti}</p>
            </div>
          )}

          {/* Partecipanti */}
          {participants.length > 0 && (
            <div className="mb-5">
              <p className="text-[10px] text-white/30 uppercase mb-2">Partecipanti ({participants.length})</p>
              <div className="flex flex-wrap gap-2">
                {participants.map((name, i) => (
                  <div key={i} className="flex items-center gap-2 bg-[#0f1d32] rounded-lg px-3 py-1.5 border border-[#1e3456]">
                    <div className="w-6 h-6 rounded-full bg-[#1a3a5c] flex items-center justify-center text-[9px] font-bold text-[#5ba8d9]">{name.split(' ').map(w=>w[0]).join('')}</div>
                    <span className="text-xs text-white/60">{name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Documenti */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] text-white/30 uppercase">Documenti Allegati</p>
              <div>
                <input id={`bando-doc-${detail.id}`} type="file" onChange={e => uploadDoc(e, detail.id)} className="hidden" />
                <Button size="sm" variant="outline" onClick={() => document.getElementById(`bando-doc-${detail.id}`).click()}
                  className="text-xs border-[#1e3456] text-white/40 bg-transparent hover:bg-[#1a3a5c]"><Upload className="w-3 h-3 mr-1" /> Carica</Button>
              </div>
            </div>
          </div>

          {/* Valutazioni */}
          <ValutazioniSection tipo="bando" rifId={detail.id} />

          {/* Commenti */}
          <CommentiSection tipo="bando" rifId={detail.id} />
        </div>
      </div>
    );
  }

  // ─── List View ───
  return (
    <div data-testid="bandi-management">
      <div className="flex items-center justify-between mb-6">
        <div className="relative flex-1 max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" /><Input placeholder="Cerca bandi..." value={search} onChange={e => { setSearch(e.target.value); pg.resetPage(); }} className="pl-10 bg-white/5 border-[#1e3456] text-white placeholder:text-white/20" data-testid="search-bandi" /></div>
        <Button data-testid="create-bando-btn" onClick={openCreate} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 font-medium">
          <Plus className="w-4 h-4 mr-2" /> Nuovo Bando
        </Button>
      </div>
      <div className="space-y-2">
        {pg.paged.map(b => {
          const days = daysLeft(b.scadenza);
          const prog = b.avanzamento || 0;
          return (
            <div key={b.id} className="bg-[#13243d] border border-[#1e3456] rounded-xl p-4 cursor-pointer hover:border-[#2a5080] hover:shadow-lg hover:shadow-black/15 transition-all" onClick={() => setDetail(b)} data-testid={`bando-row-${b.id}`}>
              <div className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-white truncate">{b.titolo}</p>
                    <Badge className={`text-[10px] border ${b.stato === "aperto" ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>{b.stato}</Badge>
                  </div>
                  <div className="flex items-center gap-4 text-[10px] text-white/30">
                    <span>{b.tipo}</span>
                    {b.scadenza && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{b.scadenza}</span>}
                    {days !== null && <span className={days > 7 ? 'text-emerald-400/60' : days > 0 ? 'text-amber-400/60' : 'text-red-400/60'}>{days > 0 ? `${days}g` : 'Scaduto'}</span>}
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{b.partecipanti?.length || 0}</span>
                  </div>
                  {prog > 0 && <div className="mt-1.5 flex items-center gap-2"><div className="w-20 h-1 bg-[#1e3456] rounded-full overflow-hidden"><div className="h-full bg-[#5ba8d9] rounded-full" style={{width:`${prog}%`}} /></div><span className="text-[9px] text-[#5ba8d9]">{prog}%</span></div>}
                </div>
                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(b)} className="text-white/20 hover:text-[#5ba8d9] hover:bg-[#1a3a5c]"><Edit className="w-3.5 h-3.5" /></Button>
                  {userRole === "admin" && <Button size="sm" variant="ghost" onClick={() => handleDelete(b.id)} className="text-white/20 hover:text-red-400 hover:bg-red-500/5"><Trash2 className="w-3.5 h-3.5" /></Button>}
                </div>
              </div>
            </div>
          );
        })}
        {bandi.length === 0 && <div className="text-center py-12 text-white/30">Nessun bando</div>}
      </div>
      <PaginationBar {...pg} />

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg bg-[#13243d] border-[#1e3456] text-white" data-testid="bando-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>{editing ? "Modifica Bando" : "Nuovo Bando"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div><Label className="text-xs text-white/60">Titolo *</Label><Input data-testid="bando-titolo" value={form.titolo} onChange={e => setForm(p => ({ ...p, titolo: e.target.value }))} className="mt-1 bg-white/5 border-[#1e3456] text-white" /></div>
            <div><Label className="text-xs text-white/60">Descrizione *</Label><Textarea data-testid="bando-descrizione" value={form.descrizione} onChange={e => setForm(p => ({ ...p, descrizione: e.target.value }))} className="mt-1 bg-white/5 border-[#1e3456] text-white" rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-white/60">Scadenza</Label><Input type="date" value={form.scadenza} onChange={e => setForm(p => ({ ...p, scadenza: e.target.value }))} className="mt-1 bg-white/5 border-[#1e3456] text-white" /></div>
              <div><Label className="text-xs text-white/60">Tipo</Label><Input value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo: e.target.value }))} className="mt-1 bg-white/5 border-[#1e3456] text-white" /></div>
            </div>
            <div><Label className="text-xs text-white/60">Requisiti</Label><Textarea value={form.requisiti} onChange={e => setForm(p => ({ ...p, requisiti: e.target.value }))} className="mt-1 bg-white/5 border-[#1e3456] text-white" rows={2} /></div>
            <div>
              <div className="flex items-center justify-between mb-1"><Label className="text-xs text-white/60">Avanzamento</Label><span className="text-xs font-bold text-[#5ba8d9]">{form.avanzamento || 0}%</span></div>
              <input type="range" min={0} max={100} step={5} value={form.avanzamento || 0} onChange={e => setForm(p => ({ ...p, avanzamento: parseInt(e.target.value) }))} className="w-full h-1.5 bg-[#1e3456] rounded-full appearance-none cursor-pointer accent-[#5ba8d9]" />
            </div>
            {editing && (
              <div><Label className="text-xs text-white/60">Stato</Label>
                <Select value={form.stato || editing?.stato || "aperto"} onValueChange={v => setForm(p => ({ ...p, stato: v }))}>
                  <SelectTrigger className="mt-1 bg-white/5 border-[#1e3456] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#13243d] border-[#1e3456] text-white"><SelectItem value="aperto">Aperto</SelectItem><SelectItem value="chiuso">Chiuso</SelectItem></SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} className="border-[#1e3456] text-white/50 bg-transparent">Annulla</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#C9A84C] text-[#001830]">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Salva" : "Crea")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ── Commenti Section (riusabile) ── */
function CommentiSection({ tipo, rifId }) {
  const [commenti, setCommenti] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (rifId) axios.get(`${API}/commenti/${tipo}/${rifId}`).then(r => setCommenti(r.data)).catch(() => {});
  }, [tipo, rifId]);

  const send = async () => {
    if (!newComment.trim()) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/commenti`, { testo: newComment, riferimento_tipo: tipo, riferimento_id: rifId });
      setCommenti(prev => [...prev, data]);
      setNewComment("");
      toast.success("Commento aggiunto");
    } catch { toast.error("Errore"); }
    setSending(false);
  };

  const del = async (id) => {
    try { await axios.delete(`${API}/commenti/${id}`); setCommenti(prev => prev.filter(c => c.id !== id)); } catch (e) { console.error(e); }
  };

  return (
    <div className="mt-5" data-testid="commenti-section">
      <p className="text-[10px] text-white/30 uppercase mb-3 flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Commenti ({commenti.length})</p>
      <div className="space-y-2 mb-3 max-h-60 overflow-y-auto">
        {commenti.map(c => (
          <div key={c.id} className="bg-[#0f1d32] rounded-lg p-3 border border-[#1e3456]">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-[#1a3a5c] flex items-center justify-center text-[8px] font-bold text-[#5ba8d9]">{c.autore_nome?.split(' ').map(w=>w[0]).join('')}</div>
                <span className="text-xs font-medium text-white/60">{c.autore_nome}</span>
                <span className="text-[9px] text-white/20">{new Date(c.created_at).toLocaleString('it-IT', {day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}</span>
              </div>
              <Button size="sm" variant="ghost" onClick={() => del(c.id)} className="text-white/15 hover:text-red-400 h-5 w-5 p-0"><Trash2 className="w-3 h-3" /></Button>
            </div>
            <p className="text-sm text-white/50 ml-7">{c.testo}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Scrivi un commento..."
          className="bg-white/5 border-[#1e3456] text-white placeholder:text-white/20 text-sm" onKeyDown={e => e.key === 'Enter' && send()} data-testid="comment-input" />
        <Button onClick={send} disabled={sending} size="sm" className="bg-[#1a3a5c] text-[#5ba8d9] hover:bg-[#2a5080] border border-[#2a5080]" data-testid="send-comment">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </Button>
      </div>
    </div>
  );
}

/* ── Valutazioni Section (riusabile) ── */
function ValutazioniSection({ tipo, rifId }) {
  const [data, setData] = useState({ valutazioni: [], media: 0, totale: 0, mia_valutazione: null });
  const [myVote, setMyVote] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (rifId) axios.get(`${API}/valutazioni/${tipo}/${rifId}`).then(r => {
      setData(r.data);
      if (r.data.mia_valutazione) { setMyVote(r.data.mia_valutazione.voto); setMyComment(r.data.mia_valutazione.commento || ""); }
    }).catch(() => {});
  }, [tipo, rifId]);

  const send = async () => {
    if (!myVote) return;
    setSending(true);
    try {
      await axios.post(`${API}/valutazioni`, { riferimento_tipo: tipo, riferimento_id: rifId, voto: myVote, commento: myComment });
      toast.success("Valutazione salvata!");
      const { data: d } = await axios.get(`${API}/valutazioni/${tipo}/${rifId}`);
      setData(d);
    } catch { toast.error("Errore"); }
    setSending(false);
  };

  return (
    <div className="mt-5 bg-[#0f1d32] rounded-xl p-4 border border-[#1e3456]" data-testid="valutazioni-section">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] text-white/30 uppercase flex items-center gap-1.5"><Star className="w-3.5 h-3.5" /> Valutazioni</p>
        {data.totale > 0 && (
          <div className="flex items-center gap-2">
            <div className="flex">{[1,2,3,4,5].map(s => <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(data.media) ? 'text-[#C9A84C] fill-[#C9A84C]' : 'text-white/10'}`} />)}</div>
            <span className="text-xs text-[#C9A84C] font-bold">{data.media}/5</span>
            <span className="text-[9px] text-white/20">({data.totale})</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          {[1,2,3,4,5].map(s => (
            <button key={s} onClick={() => setMyVote(s)} className="p-0.5" data-testid={`star-${s}`}>
              <Star className={`w-5 h-5 transition-colors ${s <= myVote ? 'text-[#C9A84C] fill-[#C9A84C]' : 'text-white/15 hover:text-[#C9A84C]/50'}`} />
            </button>
          ))}
        </div>
        <Input value={myComment} onChange={e => setMyComment(e.target.value)} placeholder="Feedback..."
          className="flex-1 bg-white/5 border-[#1e3456] text-white text-xs placeholder:text-white/15 h-8" />
        <Button onClick={send} disabled={sending || !myVote} size="sm" className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 h-8 text-xs">
          {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Valuta"}
        </Button>
      </div>
    </div>
  );
}
